import React from "react";
import { MultiValue, SingleValue } from "react-select";

import useDebounce from "../../../hooks/useDebounce";
import {
  AiInterviewerCreatedDateRange,
  AiInterviewerStatus,
} from "../../graphql";

export type FilterOption = {
  label: string;
  value: string;
};

const DEFAULT_ITEMS_TO_SHOW = "50";
const ALLOWED_ITEMS_TO_SHOW = new Set(["50", "100", "150", "200"]);

const parsePositiveInt = (value: string | null, fallback: number): number => {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1 || !Number.isInteger(parsed)) {
    return fallback;
  }

  return parsed;
};

const parseCsvParam = (value: string | null): string[] => {
  if (!value) {
    return [];
  }

  const seen = new Set<string>();
  const parsedValues: string[] = [];

  value
    .split(",")
    .map((entry) => entry.trim())
    .forEach((entry) => {
      if (!entry || seen.has(entry)) {
        return;
      }
      seen.add(entry);
      parsedValues.push(entry);
    });

  return parsedValues;
};

const isValidCreatedDateRange = (
  value: string | null
): value is AiInterviewerCreatedDateRange => {
  return (
    value === AiInterviewerCreatedDateRange.PastWeek ||
    value === AiInterviewerCreatedDateRange.PastMonth ||
    value === AiInterviewerCreatedDateRange.PastYear
  );
};

export const isValidStatus = (
  value: string | null
): value is AiInterviewerStatus => {
  return (
    value === AiInterviewerStatus.Live ||
    value === AiInterviewerStatus.Draft ||
    value === AiInterviewerStatus.Archived
  );
};

const parseStatusFilters = (value: string | null): AiInterviewerStatus[] => {
  return parseCsvParam(value).filter((status): status is AiInterviewerStatus =>
    isValidStatus(status)
  );
};

const toFilterValues = (
  selectedOptions: MultiValue<FilterOption>
): string[] => {
  return selectedOptions.map((option) => option.value);
};

type UseAiInterviewersFiltersArgs = {
  searchParams: URLSearchParams;
  setSearchParams: (
    nextSearchParams: URLSearchParams,
    navigateOptions?: { replace?: boolean }
  ) => void;
};

export const useAiInterviewersFilters = ({
  searchParams,
  setSearchParams,
}: UseAiInterviewersFiltersArgs) => {
  const [searchTerm, setSearchTerm] = React.useState(
    () => searchParams.get("q") || ""
  );
  const [statusFilters, setStatusFilters] = React.useState<
    AiInterviewerStatus[]
  >(() => parseStatusFilters(searchParams.get("status")));
  const [positionIdFilters, setPositionIdFilters] = React.useState<string[]>(
    () => parseCsvParam(searchParams.get("positionId"))
  );
  const [authorIdFilters, setAuthorIdFilters] = React.useState<string[]>(() =>
    parseCsvParam(searchParams.get("authorId"))
  );
  const [createdDateFilter, setCreatedDateFilter] = React.useState<
    AiInterviewerCreatedDateRange | ""
  >(() => {
    const createdDateRange = searchParams.get("createdDateRange");
    return isValidCreatedDateRange(createdDateRange) ? createdDateRange : "";
  });

  const [itemsToShow, setItemsToShow] = React.useState(() => {
    const show = searchParams.get("show");
    return show && ALLOWED_ITEMS_TO_SHOW.has(show)
      ? show
      : DEFAULT_ITEMS_TO_SHOW;
  });
  const [currentPage, setCurrentPage] = React.useState(() =>
    parsePositiveInt(searchParams.get("page"), 1)
  );

  const [drawerStatusFilters, setDrawerStatusFilters] = React.useState<
    AiInterviewerStatus[]
  >([]);
  const [drawerPositionFilters, setDrawerPositionFilters] = React.useState<
    string[]
  >([]);
  const [drawerAuthorFilters, setDrawerAuthorFilters] = React.useState<
    string[]
  >([]);
  const [drawerDate, setDrawerDate] = React.useState<
    AiInterviewerCreatedDateRange | ""
  >("");
  const [drawerKeyword, setDrawerKeyword] = React.useState("");

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const hasInitializedRef = React.useRef(false);

  React.useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      return;
    }

    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    statusFilters,
    positionIdFilters,
    authorIdFilters,
    createdDateFilter,
    itemsToShow,
  ]);

  React.useEffect(() => {
    const nextSearchParams = new URLSearchParams();
    if (searchTerm.trim()) {
      nextSearchParams.set("q", searchTerm.trim());
    }
    if (statusFilters.length > 0) {
      nextSearchParams.set("status", statusFilters.join(","));
    }
    if (positionIdFilters.length > 0) {
      nextSearchParams.set("positionId", positionIdFilters.join(","));
    }
    if (authorIdFilters.length > 0) {
      nextSearchParams.set("authorId", authorIdFilters.join(","));
    }
    if (createdDateFilter) {
      nextSearchParams.set("createdDateRange", createdDateFilter);
    }
    if (itemsToShow !== DEFAULT_ITEMS_TO_SHOW) {
      nextSearchParams.set("show", itemsToShow);
    }
    if (currentPage > 1) {
      nextSearchParams.set("page", String(currentPage));
    }

    setSearchParams(nextSearchParams, { replace: true });
  }, [
    searchTerm,
    statusFilters,
    positionIdFilters,
    authorIdFilters,
    createdDateFilter,
    itemsToShow,
    currentPage,
    setSearchParams,
  ]);

  const handleStatusChange = (selectedOptions: MultiValue<FilterOption>) => {
    const nextStatuses = toFilterValues(selectedOptions).filter(
      (status): status is AiInterviewerStatus => isValidStatus(status)
    );
    setStatusFilters(nextStatuses);
  };

  const handlePositionChange = (selectedOptions: MultiValue<FilterOption>) => {
    setPositionIdFilters(toFilterValues(selectedOptions));
  };

  const handleAuthorChange = (selectedOptions: MultiValue<FilterOption>) => {
    setAuthorIdFilters(toFilterValues(selectedOptions));
  };

  const handleCreatedDateChange = (
    selectedOption: SingleValue<FilterOption>
  ) => {
    const nextValue = selectedOption?.value || "";
    setCreatedDateFilter(isValidCreatedDateRange(nextValue) ? nextValue : "");
  };

  const handleDrawerStatusChange = (
    selectedOptions: MultiValue<FilterOption>
  ) => {
    const nextStatuses = toFilterValues(selectedOptions).filter(
      (status): status is AiInterviewerStatus => isValidStatus(status)
    );
    setDrawerStatusFilters(nextStatuses);
  };

  const handleDrawerPositionChange = (
    selectedOptions: MultiValue<FilterOption>
  ) => {
    setDrawerPositionFilters(toFilterValues(selectedOptions));
  };

  const handleDrawerAuthorChange = (
    selectedOptions: MultiValue<FilterOption>
  ) => {
    setDrawerAuthorFilters(toFilterValues(selectedOptions));
  };

  const handleDrawerCreatedDateChange = (
    selectedOption: SingleValue<FilterOption>
  ) => {
    const nextValue = selectedOption?.value || "";
    setDrawerDate(isValidCreatedDateRange(nextValue) ? nextValue : "");
  };

  const syncDrawerFromFilters = () => {
    setDrawerKeyword(searchTerm);
    setDrawerStatusFilters(statusFilters);
    setDrawerPositionFilters(positionIdFilters);
    setDrawerAuthorFilters(authorIdFilters);
    setDrawerDate(createdDateFilter);
  };

  const applyDrawerFilters = () => {
    setSearchTerm(drawerKeyword);
    setStatusFilters(drawerStatusFilters);
    setPositionIdFilters(drawerPositionFilters);
    setAuthorIdFilters(drawerAuthorFilters);
    setCreatedDateFilter(drawerDate);
  };

  const resetAllFiltersAndDrawer = () => {
    setSearchTerm("");
    setStatusFilters([]);
    setPositionIdFilters([]);
    setAuthorIdFilters([]);
    setCreatedDateFilter("");
    setDrawerKeyword("");
    setDrawerStatusFilters([]);
    setDrawerPositionFilters([]);
    setDrawerAuthorFilters([]);
    setDrawerDate("");
  };

  const keywordFilter = searchTerm.trim();
  const activeFilterCount =
    (keywordFilter ? 1 : 0) +
    statusFilters.length +
    positionIdFilters.length +
    authorIdFilters.length +
    (createdDateFilter ? 1 : 0);

  return {
    searchTerm,
    setSearchTerm,
    statusFilters,
    setStatusFilters,
    positionIdFilters,
    setPositionIdFilters,
    authorIdFilters,
    setAuthorIdFilters,
    createdDateFilter,
    setCreatedDateFilter,
    itemsToShow,
    setItemsToShow,
    currentPage,
    setCurrentPage,
    drawerStatusFilters,
    drawerPositionFilters,
    drawerAuthorFilters,
    drawerDate,
    drawerKeyword,
    setDrawerKeyword,
    debouncedSearchTerm,
    activeFilterCount,
    keywordFilter,
    hasFiltersApplied: searchTerm.trim().length > 0 || activeFilterCount > 0,
    handleStatusChange,
    handlePositionChange,
    handleAuthorChange,
    handleCreatedDateChange,
    handleDrawerStatusChange,
    handleDrawerPositionChange,
    handleDrawerAuthorChange,
    handleDrawerCreatedDateChange,
    syncDrawerFromFilters,
    applyDrawerFilters,
    resetAllFiltersAndDrawer,
  };
};

export type UseAiInterviewersFiltersResult = ReturnType<
  typeof useAiInterviewersFilters
>;
